import { CTAButton1 } from '../../_shared/buttons';
import { useState, useEffect, useRef, useCallback } from 'react';
import Skeleton from '@mui/material/Skeleton';

import classes from './TfaInputs.module.scss';
import EmailImage from 'assets/tfa-email.png';

import PinInput from 'react-pin-input';

const RESEND_DELAY = 30;

type DeliveryChannel = 'email' | 'sms';

type TfaInputsProps = {
    isSignUpFlow: boolean;
    inputsRef: React.MutableRefObject<HTMLDivElement | null>;
    setError: (value: boolean) => void;
    error: boolean;
    email: string;
    loading: boolean;
    code: string;
    setCode: (code: string) => void;
    sendCode: (credentials: {
        email: string;
        prefer_text?: boolean;
    }) => Promise<{ message?: string }>;
    onCodeSent?: (message: JSX.Element | string) => void;
};

const parseDeliveryChannel = (message?: string): DeliveryChannel => {
    const lowerMessage = (message || '').toLowerCase();
    if (lowerMessage.includes('phone')) {
        return 'sms';
    }
    return 'email';
};

export const TfaInputs = ({
    isSignUpFlow,
    inputsRef,
    loading,
    setError,
    error,
    email,
    code,
    setCode,
    sendCode,
    onCodeSent
}: TfaInputsProps) => {
    const pinStyle = {
        fontFamily: "Manrope",
        color: "#1F2152",
        fontSize: "1rem",
        fontWeight: "bold",
        border: "3px solid #00000000",
        background: "white",
        borderRadius: "5px",
        margin: "0px 5px 0px 5px",
        width: "55px",
        height: "60px",
    };
    const pinInputFocusStyle = {
        border: "3px solid #7378e8",
        background: "white",
        borderRadius: "5px",
        transform: "scale(1.1)",
        margin: "0px 5px 0px 5px"
    };

    const pinRef = useRef<PinInput | null>(null);
    const hasSentInitialCode = useRef(false);

    const [codeSet, setCodeSet] = useState<boolean>(false);
    const [secondsLeft, setSecondsLeft] = useState(RESEND_DELAY);
    const [isSendingCode, setIsSendingCode] = useState(true);
    const [deliveryChannel, setDeliveryChannel] = useState<DeliveryChannel | null>(null);
    const resendDisabled = secondsLeft > 0;

    const getToastMessage = useCallback(
        (channel: DeliveryChannel) => {
            if (channel === 'sms') {
                return "We've sent a new code to the phone number associated with this account";
            }
            return (
                <>
                    We&apos;ve sent a new code to <b>{email}</b>
                </>
            );
        },
        [email]
    );

    const handleSendCode = useCallback(
        async (prefer_text?: boolean): Promise<DeliveryChannel | null> => {
            setIsSendingCode(true);
            try {
                const credentials =
                    typeof prefer_text === 'boolean'
                        ? { email, prefer_text }
                        : { email };
                const data = await sendCode(credentials);
                const channel = parseDeliveryChannel(data?.message);
                setDeliveryChannel(channel);
                setSecondsLeft(RESEND_DELAY);
                return channel;
            } catch (err) {
                console.error(err);
                return null;
            } finally {
                setIsSendingCode(false);
            }
        },
        [email, sendCode]
    );

    useEffect(() => {
        if (hasSentInitialCode.current) return;
        hasSentInitialCode.current = true;
        handleSendCode();
    }, [handleSendCode]);

    useEffect(() => {
        if (!resendDisabled) return;

        const interval = setInterval(() => {
            setSecondsLeft(prev => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [resendDisabled]);

    useEffect(() => {
        setCodeSet(code.length !== 6);
    }, [code]);

    const showCodeSentToast = (channel: DeliveryChannel) => {
        onCodeSent?.(getToastMessage(channel));
    };

    const handleResend = async () => {
        if (resendDisabled || isSendingCode) return;

        pinRef.current?.clear();
        setCode('');

        // Resend without prefer_text — same as initial send
        const channel = await handleSendCode();
        if (channel) showCodeSentToast(channel);
    };

    const handleSwitchChannel = async () => {
        if (isSendingCode) return;

        pinRef.current?.clear();
        setCode('');

        const nextPreferText = deliveryChannel === 'email';
        const channel = await handleSendCode(nextPreferText);
        if (channel) showCodeSentToast(channel);
    };

    if (isSendingCode && !deliveryChannel) {
        return (
            <div className={classes.tfaInputsDiv} ref={inputsRef}>
                <Skeleton
                    variant="rectangular"
                    width={130}
                    height={116}
                    sx={{ marginBottom: '36px', borderRadius: '8px' }}
                />
                <Skeleton
                    variant="text"
                    width="70%"
                    height={32}
                    sx={{ marginBottom: '24px' }}
                />
                <Skeleton
                    variant="text"
                    width="90%"
                    height={20}
                />
                <Skeleton
                    variant="text"
                    width="80%"
                    height={20}
                    sx={{ marginBottom: '32px' }}
                />
                <div className={classes.skeletonPinRow}>
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Skeleton
                            key={i}
                            variant="rectangular"
                            width={55}
                            height={60}
                            sx={{ borderRadius: '5px', margin: '0 5px' }}
                        />
                    ))}
                </div>
                <Skeleton
                    variant="rectangular"
                    width={180}
                    height={44}
                    sx={{ marginBottom: '16px', borderRadius: '4px' }}
                />
                <Skeleton variant="text" width={120} height={18} />
            </div>
        );
    }

    return (
        <div className={classes.tfaInputsDiv} ref={inputsRef}>
            <img
                className={classes.emailImage}
                src={EmailImage}
                alt="external id card"
            />
            <div className={classes.headerText}>
                {isSignUpFlow
                    ? deliveryChannel === 'sms'
                        ? 'First, we need to verify your phone number'
                        : 'First, we need to verify your email address'
                    : "First, let's verify it's you"}
            </div>

            <div className={classes.description}>
                {deliveryChannel === 'sms' ? (
                    <>
                        We&apos;ve sent a 6-digit two factor authentication code to
                        the phone number associated with this account. If you&apos;d
                        like to change that number please contact support.
                    </>
                ) : (
                    <>
                        We&apos;ve sent a 6-digit two factor authentication code to
                        {' '}
                        <b>{email}</b>, please enter the code below. If you don&apos;t
                        see it in your inbox, make sure to check the spam folder.
                    </>
                )}
            </div>

            <PinInput
                length={6}
                ref={(n) => (pinRef.current = n)}
                onChange={(value) => {
                    if (error) setError(false);
                    setCode(value);
                }}
                onComplete={(value) => setCode(value)}
                type="numeric"
                inputMode="number"
                placeholder="X"
                style={{marginBottom: "64px",  padding: "0px, 0px, 0px, 0px"}}
                inputStyle={pinStyle}
                inputFocusStyle={pinInputFocusStyle}
            />
            {error &&
                <div className={classes.errorDiv}>
                    <span className={classes.error}>
                        Incorrect code. Please try again.
                    </span>
                </div>
            }
            <CTAButton1
                // invert
                type="submit"
                className={`
                    ${classes.submitButton}
                    ${!codeSet && !loading ? classes.submitButtonEnabled : classes.submitButtonDisabled}
                `}
                disabled={
                    (!codeSet || !loading )  ? false : true
                }
            >
                {loading ? 'Submitting' : 'Submit Code'}
            </CTAButton1>

            <div className={classes.actionsColumn}>
                <div className={classes.actionLine}>
                    Didn&apos;t receive your code?{' '}
                    <span
                        className={`${classes.actionLink} ${
                            resendDisabled || isSendingCode ? classes.resendDisabled : ''
                        }`}
                        onClick={handleResend}
                    >
                        {resendDisabled
                            ? `Resend code (${secondsLeft}s)`
                            : 'Resend code'
                        }
                    </span>
                </div>
                {deliveryChannel && (
                    <div className={classes.actionLine}>
                        {deliveryChannel === 'email'
                            ? "Can't access this email? "
                            : "Can't access this phone? "}
                        <span
                            className={`${classes.actionLink} ${
                                isSendingCode ? classes.resendDisabled : ''
                            }`}
                            onClick={handleSwitchChannel}
                        >
                            {deliveryChannel === 'email'
                                ? 'Verify by phone'
                                : 'Verify by email'}
                        </span>
                    </div>
                )}
            </div>
        </div>
    )
};

export default TfaInputs;
